import {NarrowPage} from "../../elements/Pages";
import {React, useEffect, useState} from "react";
import Joi from "joi"
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {Input, Select, TextArea} from "../../elements/forms/Inputs"
import {FormSectionContent} from "../../elements/forms/FormLayout"
import Form, {baseHandleChange} from "../../elements/forms/Form"
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import {addFileToDocument, deleteDocument, getDocument, patchDocument, postDocument,} from "../../../api/documents";
import {getTenantsWithClients} from "../../../api/accounts";
import {urlRegEx} from "../../../utils/regularExpressions";
import {useAuth} from "../../../contexts/AuthContext";


export default function DocumentCreateEdit() {

    const {currentUser, permissions} = useAuth()
    const queryClient = useQueryClient()
    const {id} = useParams()
    const [data, setData] = useState({
        id: null,
        tenant: null,
        client: null,
        title: "",
        processed_content: "",
        source_url: ""
    })

    const {data: tenants} = useQuery(['tenants_with_clients', currentUser.id], getTenantsWithClients, {refetchInterval: 1000 * 60 * 60 * 24})

    const [selectedFile, setSelectedFile] = useState();
    const [isFilePicked, setIsFilePicked] = useState(false);

    const [errors, setErrors] = useState({}) // validation errors

    let navigate = useNavigate();

    useEffect(() => {
        loadData()
    }, [id])

    useEffect(() => {
        // Preselect tenant if there's only one and we're creating a new document
        if (!id && !data.tenant && tenants && tenants.length === 1) {
            setData(prevData => ({
                ...prevData,
                tenant: tenants[0].id
            }))
        }
    }, [tenants, id, data.tenant])

    async function loadData() {
        if (!id) return
        const newData = await getDocument(id)
        setData(newData)
    }

    const schema = {
        tenant: Joi.number().required().messages({"number.base": "Please select a team"}),
        client: Joi.object().allow(null).label("Client"),
        title: Joi.string().required().label("Title"),
        source_url: Joi.string().regex(urlRegEx).max(500).allow(null, '')
            .messages({
                "object.regex": "Please enter a valid URL starting with https://",
                "string.pattern.base": "Please enter a valid URL starting with https://"
            }),
        processed_content: Joi.string().allow('').custom((value, helpers) => {
            // Check if either file is selected or processed_content has value
            const hasFile = isFilePicked || data.file;
            const hasContent = value && value.trim().length > 0;

            if (!hasFile && !hasContent) {
                return helpers.error('any.custom', {
                    message: 'Either a file must be uploaded or content must be provided'
                });
            }

            return value;
        })
    };

    const title = (id || data.id) ? "Edit document" : "Add new document"
    const successMessage = (id || data.id) ? "Document updated" : "Uploaded new document"

    async function onSave() {
        // console.log(data)

        // saving the instance first
        let newData
        if (!data.id) newData = await postDocument(data)
        else newData = await patchDocument(data) // TODO

        // then adding the file
        if (isFilePicked) {
            const formData = new FormData();
            formData.append('file', selectedFile);
            newData = await addFileToDocument(formData, newData.id)
        }

        setData(newData)
        toast.success(successMessage)
        queryClient.invalidateQueries({ queryKey: ["direct_upload_documents", currentUser.id] })
        navigate(`/documents`)
    }

    async function onDelete() {
        await deleteDocument(data.id)
        queryClient.invalidateQueries({ queryKey: ["direct_upload_documents", currentUser.id] })
        toast.success("Document deleted.")
        navigate(`/documents`)
    }

    function handleChange(evt) {
        baseHandleChange(evt, data, setData, errors, setErrors, schema)
    }

    const handleChangeFile = (event) => {
        setSelectedFile(event.target.files[0])
        setIsFilePicked(true)
    }

    return (

        <NarrowPage title={title}>

            <Form id="createEditDocument" onSave={onSave}
                  onDelete={onDelete}
                  data={data} setData={setData}
                  errors={errors} setErrors={setErrors}
                  successMessage={successMessage}
                  schema={schema}>

                <FormSectionContent>

                    <Select
                        path="tenant"
                        label="Team"
                        className="sm:col-span-3 text-left"
                        options={tenants}
                        valueAttr="id"
                        labelAttr="name"
                        onChange={handleChange}
                        data={data}
                        errors={errors}
                        isClearable={false}
                        valueOnly={true}
                    />

                    <Select
                        path="client"
                        label="Client (optional)"
                        className="sm:col-span-3 text-left"
                        options={data.tenant ? tenants?.find(t => t.id === data.tenant)?.clients || [] : []}
                        valueAttr="id"
                        labelAttr="name"
                        onChange={handleChange}
                        data={data}
                        errors={errors}
                        isClearable={true}
                        valueOnly={false}
                    />

                    <Input
                        path="title"
                        type="text"
                        label="Title"
                        className="sm:col-span-6"
                        onChange={handleChange}
                        errors={errors}
                        data={data}
                    />

                    <Input
                        path="source_url"
                        type="text"
                        label="Source URL"
                        helpText="Will link to this URL for reference when this document is used (optional)"
                        className="sm:col-span-6"
                        onChange={handleChange}
                        errors={errors}
                        data={data}
                    />

                    <TextArea
                        path="processed_content"
                        label="Content"
                        helpText="Paste the content directly (rather than the file)"
                        className="sm:col-span-6"
                        onChange={handleChange}
                        errors={errors}
                        data={data}
                        rows={4}
                    />

                    <div className="sm:col-span-6 text-sm">
                        <input type="file" name="file" onChange={handleChangeFile}/> <br/>

                        <div>
                            {data.file && <p className="py-1">Current file: {data.file}</p>}
                            {data.file_name && <p className="py-1">Current file: {data.file_name}</p>}
                        </div>
                    </div>

                </FormSectionContent>

            </Form>

            <span onClick={() => navigate("/documents")}
                  className="cursor-pointer float-right mt-2 font-medium text-imsblue-600">Back to list</span>

        </NarrowPage>
    )
}
